import React, { useMemo } from 'react';
import { AccordionItem, AccordionContent } from '@/components/ui/accordion';
import { Progress } from '@/components/ui/progress';
import chevronDownIcon from '@/assets/icons/chevron-down.svg';
import { ACType } from '@/constants/accounting-centers';
import { useNavigate, useParams } from 'react-router-dom';

import departmentACIcon from '@/assets/icons/department-acs.svg';
import productsACIcon from '@/assets/icons/product-and-services.svg';
import projectsACIcon from '@/assets/icons/project-acs.svg';
import contractsACIcon from '@/assets/icons/contracts-acs.svg';
import { AC_OVERVIEW_PROGRESS_COLORS } from '@/utils/accountingCenterOverviewProgress';

interface ACProgressMetricProps {
  label: string;
  value: number;
  fillColor: string;
  trackColor?: string;
}

const ACProgressMetric: React.FC<ACProgressMetricProps> = ({ label, value, fillColor, trackColor }) => (
  <div className="px-6 py-4 border-b border-lightGray last:border-b-0">
    <div className="flex items-center justify-between gap-4 mb-2">
      <div className="flex items-center gap-2 min-w-0">
        <span className="size-2 rounded-full shrink-0" style={{ backgroundColor: fillColor }} />
        <span className="text-xs font-medium text-gray truncate">{label}</span>
      </div>
      <span className="text-[28px] font-bold text-gray leading-none shrink-0">{value}%</span>
    </div>
    <Progress
      value={value}
      className="h-4 rounded-[4px] bg-primary"
      indicatorClassName="rounded-[4px]"
      indicatorStyle={{ backgroundColor: fillColor }}
      style={trackColor ? { backgroundColor: trackColor } : undefined}
    />
  </div>
);

interface ACItemAccordionProps {
  ac: any;
  acCenter: {
    type: ACType;
    acmev?: number;
    acmev_used?: number;
    acrt_achieved?: number;
  };
  graphViewMode: string;
  open: boolean;
  onToggle: () => void;
  budget: any;
}

const ACItemAccordion = ({ ac, acCenter, open, onToggle, budget }: ACItemAccordionProps) => {
  const navigate = useNavigate();
  const { budgetId, acType } = useParams();

  const progressMetrics = useMemo(() => {
    const acmevRemaining = ac?.acmev ?? 0;
    const acmevUsed = ac?.acmev_used ?? 0;
    const acmevTotal = acmevRemaining + acmevUsed;
    const remainingAcmevPercent =
      acmevTotal > 0 ? Math.round((acmevRemaining / acmevTotal) * 100) : acmevRemaining > 0 ? 100 : 0;

    const acrtTotal = ac?.acrt ?? 0;
    const acrtAchieved = ac?.acrt_achieved ?? 0;
    const achievedAcrtPercent = acrtTotal > 0 ? Math.round((acrtAchieved / acrtTotal) * 100) : 0;

    const acohTotal = ac?.acoh ?? 0;
    const acohUsed = ac?.acoh_used ?? 0;
    const acohUsedPercent = acohTotal > 0 ? Math.round((acohUsed / acohTotal) * 100) : 0;

    return {
      remainingAcmevPercent,
      achievedAcrtPercent,
      acohUsedPercent
    };
  }, [ac]);

  const getIcon = (type: ACType) => {
    switch (type) {
      case 'DEPARTMENT_ACCOUNTING':
        return departmentACIcon;
      case 'PRODUCTS_AND_SERVICES':
        return productsACIcon;
      case 'PROJECTS':
        return projectsACIcon;
      case 'CONTRACTS':
        return contractsACIcon;
      default:
        return departmentACIcon;
    }
  };

  return (
    <AccordionItem value={ac._id} className="border rounded-[8px] min-h-[88px] transition-all duration-300">
      <div className="flex items-center select-none py-0 pr-3">
        <div className="flex-shrink-0 px-5">
          <img className="min-w-[40px] min-h-[40px] w-[40px] h-[40px]" src={getIcon(acCenter?.type)} alt="vault-icon" />
        </div>
        <div className="flex-1 min-w-0 flex flex-col gap-[0.4rem] border-l p-6 py-4 text-left">
          <button
            title={ac.name || '-'}
            onClick={() => {
              navigate(`/budgets/${budgetId}/accounting-centers/${acType}/${ac._id}`, {
                state: { acCenter: ac, budget }
              });
            }}
            className="font-bold leading-[110%] capitalize truncate w-full mb-[0.1rem] cursor-pointer text-left"
          >
            {ac.name || '-'}
          </button>
          <span className="text-xs text-secondary leading-[100%] truncate font-medium">
            (
            {(
              (((ac?.acmev || 0) + (ac?.acmev_used || 0)) / ((ac?.bmev || 0) + (ac?.bmev_used || 0) || 0)) *
              100
            )?.toFixed(0)}
            % of BMEV)
          </span>
          <span className="text-xs text-secondary leading-[100%] truncate">
            Remaining ACMEV €{ac?.acmev?.toLocaleString() || '-'}
          </span>
          <span className="text-xs text-secondary leading-[100%] truncate">
            Acheived ACRT €{ac?.acrt_achieved?.toLocaleString() || '-'}
          </span>
        </div>

        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onToggle();
          }}
          className={`flex-shrink-0 min-w-[26px] min-h-[26px] w-[26px] h-[26px] flex items-center justify-center border rounded-full bg-lightPurple-200 ml-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
          aria-label={open ? 'Collapse' : 'Expand'}
          aria-expanded={open}
        >
          <img src={chevronDownIcon} alt="toggle" />
        </button>
      </div>
      <AccordionContent className="border-t" style={{ display: open ? 'block' : 'none' }}>
        <div className="flex flex-col">
          <ACProgressMetric
            label="Remaining ACMEV"
            value={progressMetrics.remainingAcmevPercent}
            fillColor={AC_OVERVIEW_PROGRESS_COLORS.primary}
            trackColor={AC_OVERVIEW_PROGRESS_COLORS.acmevUsed}
          />
          <ACProgressMetric label="Achieved ACRT" value={progressMetrics.achievedAcrtPercent} fillColor="#48C80C" />
          <ACProgressMetric label="ACOH Used" value={progressMetrics.acohUsedPercent} fillColor="#FF8D28" />
        </div>
      </AccordionContent>
    </AccordionItem>
  );
};

export default ACItemAccordion;
